import { Button, type ButtonProps } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { SliderLine } from "@dropbox/dig-icons/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import { useAvailableModels } from "@mirage/service-conversation/hooks/useAvailableModels";
import type { ModelId, ModelInfo } from "@mirage/service-conversation/types";
import { ModelSource } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/conversations_pb";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import { useMediaQuery } from "@react-hookz/web";
import { memo, useMemo, useRef, useState } from "react";

type ModelOption = {
  value: string;
  label: string;
  isSelected: boolean;
};

interface ModelPickerProps {
  selectedModel: ModelId;
  setSelectedModel: (model: ModelId) => void;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  dataTestId?: string;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  isCompact?: boolean;
}

export const ModelPicker = memo(
  ({
    selectedModel,
    setSelectedModel,
    dataTestId = "model-selector",
    variant = "filled",
    size = "medium",
    logComposeEvent,
    conversationId,
    isCompact = false,
  }: ModelPickerProps) => {
    const isNarrow = useMediaQuery("(max-width: 700px)");
    const { availableModels } = useAvailableModels();
    const {
      certifiedModelOptions,
      experimentalModelOptions,
      numExperimentalModels,
      hasSelectedModel,
      modelMap,
    } = useMemo(() => {
      return getModelOptions(availableModels, selectedModel);
    }, [selectedModel, availableModels]);

    return (
      <Menu.Wrapper
        slideSubmenu={isNarrow}
        onSelection={(selection: ModelId) => {
          logComposeEvent(
            PAP_Click_DashChatAction({
              featureLine: "assist",
              dashChatAction: "change_ai_model",
              conversationId,
            }),
          );
          setSelectedModel(selection);
        }}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              {...getTriggerProps()}
              variant={variant}
              size={size}
              data-testid={dataTestId}
              withDropdownIcon
              hasNoUnderline={variant === "transparent"}
            >
              {isCompact ? (
                <UIIcon src={SliderLine} />
              ) : hasSelectedModel ? (
                modelMap.get(selectedModel)?.displayName || selectedModel
              ) : (
                i18n.t("model_picker_button_label")
              )}
            </Button>
            <Menu.Content {...getContentProps()} auto>
              <Menu.Segment>
                {certifiedModelOptions.map((option) => {
                  const model = modelMap.get(option.value);
                  // For the default option, render without tooltip
                  if (!model) {
                    return (
                      <Menu.SelectItem
                        key={option.value}
                        value={option.value}
                        selected={option.isSelected}
                      >
                        {option.label}
                      </Menu.SelectItem>
                    );
                  }
                  return (
                    <ModelSelectItem
                      key={option.value}
                      model={model}
                      isSelected={option.isSelected}
                      value={option.value}
                      label={option.label}
                    />
                  );
                })}
              </Menu.Segment>

              {numExperimentalModels > 0 && (
                <Menu.Segment
                  withLabel={i18n.t("model_picker_experimental_models_label")}
                >
                  <Menu.Submenu
                    withTriggerContent={i18n.t(
                      "model_picker_more_models_label",
                      {
                        count: numExperimentalModels,
                      },
                    )}
                  >
                    {experimentalModelOptions.map(({ source, models }) => (
                      <Menu.Segment
                        key={source}
                        withLabel={getModelSourceLabel(source)}
                      >
                        {models.map((option) => {
                          const model = modelMap.get(option.value);
                          if (!model) {
                            return (
                              <Menu.SelectItem
                                key={option.value}
                                value={option.value}
                                selected={option.isSelected}
                              >
                                {option.label}
                              </Menu.SelectItem>
                            );
                          }
                          return (
                            <ModelSelectItem
                              key={option.value}
                              model={model}
                              isSelected={option.isSelected}
                              value={option.value}
                              label={option.label}
                            />
                          );
                        })}
                      </Menu.Segment>
                    ))}
                  </Menu.Submenu>
                </Menu.Segment>
              )}
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);

ModelPicker.displayName = "ModelPicker";

const getModelSourceLabel = (source: ModelSource): string => {
  switch (source) {
    case ModelSource.DROPBOX:
      return i18n.t("model_picker_source_dropbox");
    case ModelSource.OPENAI:
      return i18n.t("model_picker_source_openai");
    case ModelSource.GROQ:
      return i18n.t("model_picker_source_groq");
    case ModelSource.BEDROCK:
      return i18n.t("model_picker_source_aws");
    case ModelSource.VERTEX_AI:
      return i18n.t("model_picker_source_google_cloud");
    case ModelSource.AZURE:
      return i18n.t("model_picker_source_microsoft_azure");
    case ModelSource.UNKNOWN:
    default:
      return i18n.t("model_picker_source_default");
  }
};

interface ModelTooltipContentProps {
  model: ModelInfo;
}

const ModelTooltipContent: React.FC<ModelTooltipContentProps> = ({ model }) => {
  return (
    <Box maxWidth="max-content">
      <Box
        as={Text}
        isBold
        size="medium"
        marginBottom="Micro Small"
        display="flex"
      >
        {model.displayName}
      </Box>
      <Box
        as={Text}
        size="small"
        color="Text Subtle"
        display="flex"
        marginBottom="Micro Small"
      >
        {getModelSourceLabel(model.modelSource)}
      </Box>
      <Box as={Text} size="small" display="flex">
        {model.description}
      </Box>
    </Box>
  );
};

interface ModelSelectItemProps {
  model: ModelInfo;
  isSelected: boolean;
  value: string;
  label: string;
}

const ModelSelectItem: React.FC<ModelSelectItemProps> = ({
  model,
  isSelected,
  value,
  label,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isMobileSize = useIsSmallSizeForSidebar();
  return (
    <div
      ref={triggerRef}
      onMouseEnter={() => setIsTooltipOpen(true)}
      onMouseLeave={() => setIsTooltipOpen(false)}
    >
      <DigTooltip.Control
        variant="rich"
        triggerRef={triggerRef}
        auto
        placement={isMobileSize ? "top" : "right"}
        openDelay={300}
        open={isTooltipOpen}
      >
        <ModelTooltipContent model={model} />
      </DigTooltip.Control>
      <Menu.SelectItem key={value} value={value} selected={isSelected}>
        {label}
      </Menu.SelectItem>
    </div>
  );
};

const getModelOptions = (
  availableModels: { models: ModelInfo[] },
  selectedModel: ModelId,
) => {
  const defaultModel = {
    value: "",
    label: "Auto",
    isSelected: !selectedModel,
  };

  const certifiedModels: ModelOption[] = availableModels.models
    .filter((model) => model.isCertified)
    .map((model) => {
      return {
        value: model.id,
        label: model.displayName,
        isSelected: selectedModel === model.id,
      };
    });

  const { experimentalModelOptions, numExperimentalModels } =
    groupExperimentalModelsBySource(availableModels.models, selectedModel);

  const hasSelectedModel = availableModels.models.some(
    (model) => selectedModel === model.id,
  );

  const certifiedModelOptions: ModelOption[] = [
    defaultModel,
    ...certifiedModels,
  ];

  // Create a map for quick model lookup
  const modelMap = new Map<string, ModelInfo>();
  availableModels.models.forEach((model) => {
    modelMap.set(model.id, model);
  });

  return {
    certifiedModelOptions,
    experimentalModelOptions,
    numExperimentalModels,
    hasSelectedModel,
    modelMap,
  };
};

const groupExperimentalModelsBySource = (
  models: ModelInfo[],
  selectedModel: ModelId,
) => {
  const experimentalModels = models.filter((model) => !model.isCertified);

  // group model options by source { source, models[] }
  const experimentalModelOptions = Array.from(
    experimentalModels.reduce((acc, model) => {
      const source = model.modelSource || ModelSource.UNKNOWN;
      if (!acc.has(source)) {
        acc.set(source, []);
      }
      acc.get(source)?.push({
        value: model.id,
        label: model.displayName,
        isSelected: selectedModel === model.id,
      });
      return acc;
    }, new Map<ModelSource, ModelOption[]>()),
  ).map(([source, models]) => {
    return {
      source,
      models,
    };
  });

  return {
    experimentalModelOptions,
    numExperimentalModels: experimentalModels.length,
  };
};
